import React, { useState, useEffect } from 'react';
import {
  MessageSquare, Mail, MailOpen, Phone, PhoneCall,
  Calendar, ArrowRight, Edit3, CheckSquare, CheckCircle,
  Loader2, Filter, Search, Send, X, MessageCircle,
} from 'lucide-react';
import { commApi } from '../../services/crmApi';

interface TimelineEvent {
  id: number;
  event_type: string;
  description: string;
  actor: string;
  direction?: string;
  recipient?: string;
  status?: string;
  field_name?: string;
  old_value?: string;
  new_value?: string;
  is_pinned?: boolean;
  extra_date?: string;
  workspace_account?: string;
  priority?: string;
  source: string;
  created_at: string;
}

const EVENT_CONFIG: Record<string, { icon: React.ElementType; color: string; bg: string; label: string }> = {
  comment: { icon: MessageSquare, color: 'text-blue-600', bg: 'bg-blue-100', label: 'Komentarz' },
  comm_email: { icon: Mail, color: 'text-indigo-600', bg: 'bg-indigo-100', label: 'Email' },
  comm_phone: { icon: PhoneCall, color: 'text-orange-600', bg: 'bg-orange-100', label: 'Telefon' },
  comm_sms: { icon: Send, color: 'text-purple-600', bg: 'bg-purple-100', label: 'SMS' },
  comm_note: { icon: Edit3, color: 'text-gray-600', bg: 'bg-gray-200', label: 'Notatka' },
  sms: { icon: Send, color: 'text-purple-600', bg: 'bg-purple-100', label: 'SMS' },
  meeting: { icon: Calendar, color: 'text-teal-600', bg: 'bg-teal-100', label: 'Spotkanie' },
  status_change: { icon: ArrowRight, color: 'text-amber-600', bg: 'bg-amber-100', label: 'Zmiana statusu' },
  field_change: { icon: Edit3, color: 'text-gray-500', bg: 'bg-gray-100', label: 'Zmiana pola' },
  email_in: { icon: MailOpen, color: 'text-green-600', bg: 'bg-green-100', label: 'Email przychodzący' },
  email_out: { icon: Mail, color: 'text-indigo-600', bg: 'bg-indigo-100', label: 'Email wychodzący' },
  task_new: { icon: CheckSquare, color: 'text-blue-600', bg: 'bg-blue-100', label: 'Nowe zadanie' },
  task_done: { icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-100', label: 'Zadanie zakończone' },
};

const FILTER_OPTIONS = [
  { value: 'all', label: 'Wszystko' },
  { value: 'comment', label: 'Komentarze' },
  { value: 'comm_', label: 'Komunikacja' },
  { value: 'email_', label: 'Emaile' },
  { value: 'sms', label: 'SMS' },
  { value: 'meeting', label: 'Spotkania' },
  { value: 'status_change', label: 'Zmiany statusu' },
  { value: 'field_change', label: 'Zmiany pol' },
  { value: 'task_', label: 'Zadania' },
];

function formatTimelineDate(dateStr: string): string {
  try {
    const d = new Date(dateStr);
    const now = new Date();
    const diff = now.getTime() - d.getTime();
    if (diff < 60000) return 'przed chwila';
    if (diff < 3600000) return `${Math.floor(diff / 60000)} min temu`;
    if (diff < 86400000 && d.getDate() === now.getDate()) {
      return `dzis ${d.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' })}`;
    }
    if (diff < 172800000) {
      return `wczoraj ${d.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' })}`;
    }
    return d.toLocaleDateString('pl-PL', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch {
    return dateStr;
  }
}

function groupByDate(events: TimelineEvent[]): { label: string; events: TimelineEvent[] }[] {
  const groups: { label: string; events: TimelineEvent[] }[] = [];
  const now = new Date();

  for (const event of events) {
    const d = new Date(event.created_at);
    let label: string;
    const diff = now.getTime() - d.getTime();

    if (diff < 86400000 && d.getDate() === now.getDate()) label = 'Dzis';
    else if (diff < 172800000) label = 'Wczoraj';
    else if (diff < 604800000) label = 'Ten tydzien';
    else if (d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear()) label = 'Ten miesiac';
    else label = d.toLocaleDateString('pl-PL', { month: 'long', year: 'numeric' });

    const existing = groups.find(g => g.label === label);
    if (existing) existing.events.push(event);
    else groups.push({ label, events: [event] });
  }

  return groups;
}

function ExpandableText({ text, limit = 300 }: { text: string; limit?: number }) {
  const [expanded, setExpanded] = useState(false);
  if (text.length <= limit) return <p className="text-xs text-gray-700 leading-relaxed">{text}</p>;
  return (
    <div>
      <p className="text-xs text-gray-700 leading-relaxed">
        {expanded ? text : text.substring(0, limit) + '...'}
      </p>
      <button onClick={() => setExpanded(!expanded)} className="text-[10px] text-blue-500 hover:text-blue-700 mt-0.5">
        {expanded ? 'Zwiń' : 'Rozwiń'}
      </button>
    </div>
  );
}

export default function UnifiedTimeline({ applicationId }: { applicationId: number }) {
  const [events, setEvents] = useState<TimelineEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [visibleCount, setVisibleCount] = useState(50);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const data = await commApi.timeline(applicationId, filter !== 'all' ? filter : undefined);
        setEvents(data.events || []);
      } catch { /* */ }
      setLoading(false);
    })();
  }, [applicationId, filter]);

  const allFilteredEvents = searchTerm
    ? events.filter(e =>
        e.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        e.actor?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : events;

  const filteredEvents = allFilteredEvents.slice(0, visibleCount);
  const hasMore = allFilteredEvents.length > visibleCount;
  const groups = groupByDate(filteredEvents);

  if (loading) return <div className="flex justify-center py-8"><Loader2 size={20} className="animate-spin text-blue-400" /></div>;

  return (
    <div>
      {/* Filters */}
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <div className="relative flex-1 min-w-[120px]">
          <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            placeholder="Szukaj w osi czasu..."
            className="w-full pl-7 pr-2 py-1 text-[11px] border rounded bg-white focus:ring-1 focus:ring-blue-200"
          />
        </div>
        <div className="flex gap-0.5 flex-wrap">
          {FILTER_OPTIONS.map(opt => (
            <button
              key={opt.value}
              onClick={() => setFilter(opt.value)}
              className={`text-[10px] px-2 py-0.5 rounded-full transition-colors ${
                filter === opt.value
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Event count */}
      <div className="text-[10px] text-gray-400 mb-2">
        {filteredEvents.length} wydarzen
        {searchTerm && ` (szukanie: "${searchTerm}")`}
      </div>

      {/* Timeline */}
      {groups.length === 0 ? (
        <div className="text-center py-8 text-gray-400">
          <MessageCircle size={28} className="mx-auto mb-2 opacity-30" />
          <p className="text-xs">Brak wydarzeń</p>
        </div>
      ) : (
        <div className="space-y-4">
          {groups.map(group => (
            <div key={group.label}>
              {/* Date group header */}
              <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2 sticky top-0 bg-white/90 backdrop-blur-sm py-1">
                {group.label}
              </div>

              {/* Events in this group */}
              <div className="relative ml-3 border-l-2 border-gray-200 pl-4 space-y-3">
                {group.events.map(event => {
                  const config = EVENT_CONFIG[event.event_type] || EVENT_CONFIG.comm_note;
                  const Icon = config.icon;

                  return (
                    <div key={`${event.source}-${event.id}`} className="relative group">
                      {/* Timeline dot */}
                      <div className={`absolute -left-[22px] top-1 w-5 h-5 rounded-full ${config.bg} flex items-center justify-center`}>
                        <Icon size={11} className={config.color} />
                      </div>

                      {/* Event content */}
                      <div className="pb-1">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className={`text-[10px] font-medium ${config.color}`}>{config.label}</span>
                          <span className="text-[10px] text-gray-400">{formatTimelineDate(event.created_at)}</span>
                          {event.actor && event.actor !== 'System' && (
                            <span className="text-[10px] text-gray-400">— {event.actor}</span>
                          )}
                        </div>

                        {event.description && (
                          <ExpandableText text={event.description} />
                        )}

                        {/* Field change details */}
                        {event.event_type === 'field_change' && event.field_name && (
                          <div className="mt-1 text-[11px] bg-gray-50 rounded px-2 py-1 inline-block">
                            <span className="text-gray-500">{event.field_name}: </span>
                            {event.old_value && <span className="line-through text-red-400 mr-1">{event.old_value.substring(0, 80)}</span>}
                            {event.new_value && <span className="text-green-600">{event.new_value.substring(0, 80)}</span>}
                          </div>
                        )}

                        {/* Extra metadata */}
                        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                          {event.recipient && (
                            <span className="text-[10px] text-gray-400">do: {event.recipient}</span>
                          )}
                          {event.direction && (
                            <span className={`text-[10px] ${event.direction === 'inbound' ? 'text-green-500' : 'text-blue-500'}`}>
                              {event.direction === 'inbound' ? 'przychodzacy' : 'wychodzacy'}
                            </span>
                          )}
                          {event.workspace_account && (
                            <span className="text-[10px] text-gray-300">via {event.workspace_account}</span>
                          )}
                          {event.status === 'scheduled' && event.extra_date && (
                            <span className="text-[10px] text-teal-600">
                              {new Date(event.extra_date).toLocaleDateString('pl-PL', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
          {hasMore && (
            <button
              onClick={() => setVisibleCount(v => v + 50)}
              className="w-full py-2 text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
            >
              Załaduj więcej ({allFilteredEvents.length - visibleCount} pozostałych)
            </button>
          )}
        </div>
      )}
    </div>
  );
}
